// Report detail at `/reports/$id`. Structure from Figma Make's
// ReportDetailPage — company info, report details, downloadable documents.
//
// TODO: loader to fetch report by id. Currently renders placeholder data.

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  H1,
  P,
  Separator,
} from '@podium/platform/ui'
import { createFileRoute } from '@tanstack/react-router'

import { AlertWord } from '../components/brand'

export const Route = createFileRoute('/reports/$id')({
  component: ReportDetailPage,
})

type ReportDocument = {
  label: string
  filename: string
  sizeBytes: number
  required: boolean
}

type ReportDetail = {
  id: string
  companyName: string
  eik: string
  city: string
  address: string
  vatRegistered: boolean
  reportYear: number
  representativeName: string
  preparedBy: string | null
  certifiedBy: string | null
  publicationDate: string
  excerptNumber: string
  documents: ReportDocument[]
}

function ReportDetailPage() {
  const { id } = Route.useParams()

  // TODO: replace with loader data.
  const report: ReportDetail = {
    id,
    companyName: '—',
    eik: '—',
    city: '—',
    address: '—',
    vatRegistered: false,
    reportYear: 0,
    representativeName: '—',
    preparedBy: null,
    certifiedBy: null,
    publicationDate: '—',
    excerptNumber: '—',
    documents: [],
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <P className="mb-4 text-sm">
        <a href="/reports">← Обратно към архива</a>
      </P>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <H1>{report.companyName}</H1>
              {report.reportYear > 0 && (
                <P className="text-muted-foreground">
                  Годишен финансов отчет за {report.reportYear} година
                </P>
              )}
            </div>
            {report.documents.length > 0 && (
              <Button asChild>
                <a href={`/api/reports/${report.id}/archive.zip`}>Изтегли всички (ZIP)</a>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="space-y-2">
            <CardTitle className="text-base">Данни за предприятието</CardTitle>
            <InfoGrid>
              <InfoRow term="ЕИК / БУЛСТАТ" def={report.eik} />
              <InfoRow term="Град" def={report.city} />
              <InfoRow term="Адрес" def={report.address} />
              <InfoRow term="Регистрирана по ЗДДС" def={report.vatRegistered ? 'Да' : 'Не'} />
            </InfoGrid>
          </section>

          <Separator />

          <section className="space-y-2">
            <CardTitle className="text-base">Данни за отчета</CardTitle>
            <InfoGrid>
              <InfoRow term="Отчетна година" def={String(report.reportYear || '—')} />
              <InfoRow term="Представляващ / МОЛ" def={report.representativeName} />
              {report.preparedBy && <InfoRow term="Изготвил" def={report.preparedBy} />}
              {report.certifiedBy && <InfoRow term="Заверил" def={report.certifiedBy} />}
              <InfoRow term="Дата на публикуване" def={report.publicationDate} />
              <InfoRow term="Номер на извлечение" def={report.excerptNumber} />
            </InfoGrid>
          </section>

          <Separator />

          <section className="space-y-2">
            <CardTitle className="text-base">Документи</CardTitle>
            {report.documents.length > 0 ? (
              <ul className="space-y-2">
                {report.documents.map((doc) => (
                  <li
                    key={doc.filename}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                  >
                    <div>
                      <a
                        href={`/api/reports/${report.id}/documents/${doc.filename}`}
                        className="font-medium"
                      >
                        {doc.label}
                      </a>
                      {doc.required && (
                        <>
                          {' '}
                          <AlertWord>*</AlertWord>
                        </>
                      )}
                      <P className="text-xs text-muted-foreground">
                        {doc.filename} · {formatBytes(doc.sizeBytes)}
                      </P>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <a href={`/api/reports/${report.id}/documents/${doc.filename}`}>Изтегли</a>
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <P className="text-sm text-muted-foreground">Няма публикувани документи.</P>
            )}
          </section>

          <Separator />

          <P className="text-xs text-muted-foreground">
            Отчетът е публикуван в съответствие с изискванията на{' '}
            <a href="/law#csch-38">чл. 38 от Закона за счетоводството</a>.
          </P>
        </CardContent>
      </Card>
    </div>
  )
}

function InfoGrid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[200px_1fr]">
      {children}
    </dl>
  )
}

function InfoRow({ term, def }: { term: string; def: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{term}</dt>
      <dd className="m-0 font-medium">{def}</dd>
    </>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
